/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Constants } from 'app/core/models/constants';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { IMAGE_UPLOAD_CONFIGURATION, UploadConfiguration } from 'app/shared/components/upload/upload.models';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { setTinyMceTabIndex } from 'app/shared/helpers/tiny-mce-tabindex-helper';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { environment } from 'environments/environment';

import { BookingConfirmationDesignFormModel } from 'app/modules/design/booking-confirmation-design/booking-confirmation-design-management/models/booking-confirmation-design-form.model';
import { BookingConfirmationDesignListService } from 'app/modules/design/booking-confirmation-design/booking-confirmation-design-management/services/booking-confirmation-design-list.service';
import { SocialMediaFormListComponent } from 'app/shared/components/social-media-form-list/social-media-form-list.component';
import { ImageUploadErrorType } from 'app/shared/models/image-upload-error-type';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { CookieService } from 'app/shared/services/cookie.service';

@Component({
	selector: 'booking-confirmation-design-form',
	templateUrl: './booking-confirmation-design-form.component.html',
	styleUrls: ['./booking-confirmation-design-form.component.scss'],
	standalone: false,
})
export class BookingConfirmationDesignFormComponent extends CreateUpdateBaseComponent<BookingConfirmationDesignFormModel> implements OnInit {
	imageUploadConfiguration: UploadConfiguration = { ...IMAGE_UPLOAD_CONFIGURATION };
	tinyMceSettings: any;
	tinyMceApiKey = environment.tinyMceApiKey;
	imageValidationMessage: string;
	imageIsValid: boolean;

	constructor(
		protected fb: FormBuilder,
		private service: BookingConfirmationDesignListService,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
		private cookieService: CookieService,
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}

	// shortcuts to controls for readability
	get name(): AbstractControl {
		return this.dataForm.controls['name'];
	}
	get image(): AbstractControl {
		return this.dataForm.controls['image'];
	}
	get emailSubject(): AbstractControl {
		return this.dataForm.controls['emailSubject'];
	}
	get emailBody(): AbstractControl {
		return this.dataForm.controls['emailBody'];
	}
	get backgroundColour(): AbstractControl {
		return this.dataForm.controls['backgroundColour'];
	}
	get textColour(): AbstractControl {
		return this.dataForm.controls['textColour'];
	}
	get linksColour(): AbstractControl {
		return this.dataForm.controls['linksColour'];
	}
	get headingBackgroundColour(): AbstractControl {
		return this.dataForm.controls['headingBackgroundColour'];
	}
	get headingTextColour(): AbstractControl {
		return this.dataForm.controls['headingTextColour'];
	}
	get subheadingBackgroundColour(): AbstractControl {
		return this.dataForm.controls['subheadingBackgroundColour'];
	}
	get subheadingTextColour(): AbstractControl {
		return this.dataForm.controls['subheadingTextColour'];
	}
	get borderColour(): AbstractControl {
		return this.dataForm.controls['borderColour'];
	}
	get socialMediaArray(): FormArray {
		return this.dataForm.controls['socialMediaList'] as FormArray;
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			name: ['', [Validators.required, Validators.maxLength(200)]],
			image: ['', [Validators.required]],
			emailSubject: ['', [Validators.required, Validators.maxLength(200)]],
			emailBody: ['', [Validators.required]],
			backgroundColour: [''],
			textColour: [''],
			linksColour: [''],
			headingBackgroundColour: [''],
			headingTextColour: [''],
			subheadingBackgroundColour: [''],
			subheadingTextColour: [''],
			borderColour: [''],
			socialMediaList: this.fb.array([]),
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.imageUploadConfiguration = {
			...this.imageUploadConfiguration,
			existingFileName: this.viewModel.image ? this.viewModel.imageName || 'booking-confirmation-image.png' : 'booking-confirmation-image.png',
		};
		this.name.setValue(this.viewModel.name);
		this.image.setValue(this.viewModel.image);
		this.emailSubject.setValue(this.viewModel.emailSubject);
		this.emailBody.setValue(this.viewModel.emailBody);
		this.backgroundColour.setValue(this.viewModel.backgroundColour);
		this.textColour.setValue(this.viewModel.textColour);
		this.linksColour.setValue(this.viewModel.linksColour);
		this.headingBackgroundColour.setValue(this.viewModel.headingBackgroundColour);
		this.headingTextColour.setValue(this.viewModel.headingTextColour);
		this.subheadingBackgroundColour.setValue(this.viewModel.subheadingBackgroundColour);
		this.subheadingTextColour.setValue(this.viewModel.subheadingTextColour);
		this.borderColour.setValue(this.viewModel.borderColour);
		this.dataForm.setControl('socialMediaList', SocialMediaFormListComponent.buildSocialMediaList(this.fb, this.viewModel.socialMediaList));
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<BookingConfirmationDesignFormModel> {
		let dataModel: BookingConfirmationDesignFormModel;

		await this.service.getEditableItemDetail(id).then((response: BookingConfirmationDesignFormModel) => {
			dataModel = response;
		});

		return Promise.resolve(dataModel);
	}
	ngOnInit() {
		this.tinyMceSettings = setTinyMceTabIndex(5, this.cookieService, Constants.EditorSettings.FullSettings(this.cookieService));
		super.ngOnInit();
	}
	mceOnInit() {
		this.emailBody.markAsPristine();
	}
	setImageFileName(files: File[]): void {
		if (!files[0]) return;
		this.imageUploadConfiguration = { ...this.imageUploadConfiguration, existingFileName: files[0].name };
	}
	// overidden from CreateUpdateBaseComponent base class
	protected createUpdateModel() {
		var imageHasChanged = this.viewModel.image !== this.image.value;
		return {
			name: this.name.value,
			image: this.image.value,
			imageName: this.imageUploadConfiguration.existingFileName,
			imageHasChanged: imageHasChanged,
			emailSubject: this.emailSubject.value,
			emailBody: /*normalizeBrandTypographyHtml(*/ this.emailBody.value /*)*/,
			backgroundColour: this.backgroundColour.value,
			textColour: this.textColour.value,
			linksColour: this.linksColour.value,
			headingBackgroundColour: this.headingBackgroundColour.value,
			headingTextColour: this.headingTextColour.value,
			subheadingBackgroundColour: this.subheadingBackgroundColour.value,
			subheadingTextColour: this.subheadingTextColour.value,
			borderColour: this.borderColour.value,
			socialMediaList: this.socialMediaArray.controls.map((socialMediaItem) => {
				return {
					socialPlatformId: socialMediaItem.get('socialPlatformId').value,
					name: socialMediaItem.get('name').value,
					included: socialMediaItem.get('included').value,
					url: socialMediaItem.get('url').value,
				};
			}),

			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.updateItem(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async doCreate(dataToUpdate: any) {
		await this.service.addNew(dataToUpdate);
	}

	// overidden from CreateUpdateBaseComponent base class
	save() {
		this.dataForm.markAllAsTouched();
		var imageHasNoErrors = this.image.valid && (this.imageIsValid || this.imageIsValid === null || this.imageIsValid === undefined);
		if (imageHasNoErrors) {
			super.save();
		}
	}

	setImageValidationMessage(invalidErrorType: ImageUploadErrorType) {
		switch (invalidErrorType) {
			case ImageUploadErrorType.InvalidMimeType:
				this.imageValidationMessage = 'Image must be a jpeg or png file type';
				break;
			case ImageUploadErrorType.FileTooLarge:
				this.setImageIsValid(false);
				this.imageValidationMessage = 'Image must be less than 10MB in size';
				break;
			default:
				this.setImageIsValid(true);
				this.imageValidationMessage = '';
				break;
		}
	}
	setImageIsValid(isValid: boolean) {
		this.imageIsValid = isValid;
	}

	showImageError() {
		//specifically check for 'this.imageIsValid === false' and not just '!this.imageIsValid' as it could be undefined or null
		return this.image.touched && (this.image.invalid || this.imageIsValid === false);
	}
	imageErrorMessage() {
		if (this.showImageError()) {
			if (this.image.errors?.required) {
				return 'Image is required';
			} else {
				return this.image.errors?.upload ?? this.imageValidationMessage;
			}
		}
		return '';
	}
}
